// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — mineriumProps.jsx   ·   THE FURNISHINGS KIT
// ────────────────────────────────────────────────────────────────────
// Every placeable thing in a Minerium, as three.js primitives painted
// with cached canvas textures. No .glb, no textures on disk, no asset
// pipeline, no build step — the same recipe `ieBeingModels.jsx` is
// built to, applied to furniture instead of creatures.
//
// ── THE RULES THIS FILE OBEYS, ALL FROM ie-being-models ─────────────
//   · No boxes for organic things. Spheres, capsules and cones, scaled
//     non-uniformly and overlapped generously. The one box allowed is a
//     hair-thin one, where a box is just a line.
//   · Paint the surface, never just colour it. One cached canvas per
//     material, seeded with mulberry32, drawn once, shared by every
//     mesh that wants it.
//   · Glow is EMISSIVE. ⚠⚠ NOT ONE NEW LIGHT. Pier3D's scene runs on a
//     FIXED THREE lights, forever — "state is sold with
//     emissiveIntensity and basic materials, never by adding one" — and
//     a dynamic light multiplies every material's cost scene-wide. A
//     yard full of lanterns would be the end of the frame rate.
//   · Seeded variety is baked in useMemo and read in JSX. ⚠ NEVER call
//     a PRNG in a render body: a correctly-seeded generator called
//     during render advances on every re-render and the detail visibly
//     crawls.
//   · One useFrame for a crowd, not one per object. The yard's wind is
//     ONE hook rotating one shared group.
//
// ── ⭐ THE THREE SHELVES, DRAWN ────────────────────────────────────
// A MYTHICAL HAS NO RECIPE IN THIS FILE, and there is no way to give it
// one. `artOf()` in the catalogue resolves a Mythical to its Legendary
// predecessor's art, and `<Ascension/>` is layered over the top. So
// "same design, risen" is not a promise anybody has to keep — it is the
// only thing the renderer is capable of drawing (PIERRE_PRIME L23).
//
// Fable is flat and plain. Legendary is the best STILL thing there is —
// zero animation, by law. Mythical is the Legendary plus motion and
// flare, which is why the ascension layer is the only animated one.
// ════════════════════════════════════════════════════════════════════

import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { mulberry32, canvasTex } from "./ieBeingModels";
import { ITEMS, artOf, isAscended, SHELF_META, isWorn, sizeOf, layerOf, LAYER } from "./mineriumCatalogue";

// ════════════════════════════════════════════════════════════════════
// THE PAINT BOX  —  every texture is one cached canvas, drawn once
// ════════════════════════════════════════════════════════════════════

// The leaf-stroke primitive. Draws grain, thatch, petals, scale rows
// and cloth folds — one shape, reused everywhere, exactly as the being
// kit does it.
const stroke = (g, x, y, len, wd, ang, fill, alpha) => {
  g.save(); g.translate(x, y); g.rotate(ang);
  g.beginPath(); g.moveTo(0, -len / 2);
  g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
  g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
  g.closePath(); g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
  g.restore(); g.globalAlpha = 1;
};

const woodTex = (key, hi, mid, lo) => canvasTex(`min_wood_${key}`, 256, 256, (g) => {
  const R = mulberry32(0x5eed01);
  g.fillStyle = mid; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 26; i++) {
    const y = R() * 256;
    g.globalAlpha = 0.10 + R() * 0.16;
    g.strokeStyle = R() > 0.5 ? hi : lo;
    g.lineWidth = 0.6 + R() * 2.2;
    g.beginPath(); g.moveTo(0, y);
    g.bezierCurveTo(85, y + (R() - 0.5) * 14, 170, y + (R() - 0.5) * 14, 256, y + (R() - 0.5) * 8);
    g.stroke();
  }
  g.globalAlpha = 0.3; g.fillStyle = lo;
  for (let i = 0; i < 5; i++) g.fillRect(0, i * 52 + 2, 256, 1.6);   // plank seams
  g.globalAlpha = 1;
});

const soilTex = (key, hi, mid, lo) => canvasTex(`min_soil_${key}`, 256, 256, (g) => {
  const R = mulberry32(0x5eed02);
  const grd = g.createLinearGradient(0, 0, 0, 256);
  grd.addColorStop(0, mid); grd.addColorStop(1, lo);
  g.fillStyle = grd; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 900; i++) {
    g.globalAlpha = 0.05 + R() * 0.22;
    g.fillStyle = R() > 0.72 ? hi : lo;
    const s = 1 + R() * 3.4;
    g.fillRect(R() * 256, R() * 256, s, s);
  }
  g.globalAlpha = 0.22; g.strokeStyle = lo; g.lineWidth = 3;
  for (let i = 0; i < 6; i++) {                     // the furrows
    const y = 22 + i * 38;
    g.beginPath(); g.moveTo(6, y); g.lineTo(250, y + (R() - 0.5) * 6); g.stroke();
  }
  g.globalAlpha = 1;
});

const stoneTex = (key, hi, mid, lo) => canvasTex(`min_stone_${key}`, 256, 256, (g) => {
  const R = mulberry32(0x5eed03);
  g.fillStyle = mid; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 240; i++) {
    g.globalAlpha = 0.05 + R() * 0.2;
    g.fillStyle = R() > 0.5 ? hi : lo;
    g.beginPath(); g.arc(R() * 256, R() * 256, 2 + R() * 16, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 0.34; g.strokeStyle = lo; g.lineWidth = 1.6;
  for (let i = 0; i < 8; i++) {                     // hairline cracks
    g.beginPath(); let x = R() * 256, y = R() * 256; g.moveTo(x, y);
    for (let s = 0; s < 5; s++) { x += (R() - 0.5) * 60; y += (R() - 0.5) * 60; g.lineTo(x, y); }
    g.stroke();
  }
  g.globalAlpha = 1;
});

const clothTex = (key, hi, mid, lo) => canvasTex(`min_cloth_${key}`, 256, 256, (g) => {
  const R = mulberry32(0x5eed04);
  g.fillStyle = mid; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 150; i++) stroke(g, R() * 256, R() * 256, 26 + R() * 40, 3, R() * Math.PI, R() > 0.5 ? hi : lo, 0.06 + R() * 0.14);
  g.globalAlpha = 0.5; g.strokeStyle = hi; g.lineWidth = 5;
  g.strokeRect(12, 12, 232, 232);                   // the trim
  g.globalAlpha = 1;
});

const leafTex = (key, hi, mid, lo) => canvasTex(`min_leaf_${key}`, 256, 256, (g) => {
  const R = mulberry32(0x5eed05);
  g.fillStyle = lo; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 260; i++) stroke(g, R() * 256, R() * 256, 18 + R() * 46, 6 + R() * 6, R() * Math.PI, R() > 0.62 ? hi : mid, 0.3 + R() * 0.6);
  g.globalAlpha = 1;
});

const waterTex = (key, hi, mid, deep) => canvasTex(`min_water_${key}`, 256, 256, (g) => {
  const R = mulberry32(0x5eed06);
  const grd = g.createRadialGradient(128, 128, 8, 128, 128, 128);
  grd.addColorStop(0, hi); grd.addColorStop(0.5, mid); grd.addColorStop(1, deep);
  g.fillStyle = grd; g.fillRect(0, 0, 256, 256);
  g.globalAlpha = 0.26; g.strokeStyle = hi; g.lineWidth = 2;
  for (let i = 0; i < 16; i++) {                    // ripple rings
    g.beginPath(); g.arc(128 + (R() - 0.5) * 70, 128 + (R() - 0.5) * 70, 10 + R() * 90, 0, Math.PI * 2); g.stroke();
  }
  g.globalAlpha = 1;
});

// ── the shared materials, built once ────────────────────────────────
const M = {};
function mat(key, build) { if (!M[key]) M[key] = build(); return M[key]; }

// ⚠ ONE RENDERER AT A TIME. Every WebGLRenderer hangs a dispose listener on
// each material and texture it draws, and these live here for the whole
// session — so every Canvas that ever drew furniture (the yard, the Hall)
// stayed reachable after it unmounted (code review, 2026-09-14). Call this
// beside pierTex's releaseTextures() when a Canvas goes. Disposing frees only
// the GPU copies; the next renderer re-uploads from the same canvases.
const TEX_SLOTS = ["map", "emissiveMap", "bumpMap", "roughnessMap", "normalMap", "alphaMap"];
export function releasePropMaterials() {
  for (const key of Object.keys(M)) {
    const m = M[key];
    for (const slot of TEX_SLOTS) { try { if (m[slot]) m[slot].dispose(); } catch { /* already gone */ } }
    try { m.dispose(); } catch { /* already gone */ }
  }
}

export const woodMat  = () => mat("wood",  () => new THREE.MeshStandardMaterial({ map: woodTex("oak", "#a8794a", "#7a5535", "#4a3220"), roughness: 0.92, metalness: 0.02 }));
const paleMat  = () => mat("pale",  () => new THREE.MeshStandardMaterial({ map: stoneTex("pale", "#e8e2d2", "#c9c2b0", "#8b8478"), roughness: 0.8 }));
const darkMat  = () => mat("dark",  () => new THREE.MeshStandardMaterial({ map: stoneTex("dark", "#4a4458", "#2c2838", "#15121e"), roughness: 0.72 }));
const soilMat  = () => mat("soil",  () => new THREE.MeshStandardMaterial({ map: soilTex("bed", "#6a5238", "#453324", "#241a12"), roughness: 1 }));
const strawMat = () => mat("straw", () => new THREE.MeshStandardMaterial({ map: clothTex("straw", "#e0c883", "#b89a58", "#7a6436"), roughness: 0.95 }));
const rugMat   = () => mat("rug",   () => new THREE.MeshStandardMaterial({ map: clothTex("rug", "#c9a84c", "#7a4a52", "#3a2028"), roughness: 0.98 }));
const brassMat = () => mat("brass", () => new THREE.MeshStandardMaterial({ color: "#c9a84c", roughness: 0.34, metalness: 0.72, emissive: "#4a3208", emissiveIntensity: 0.25 }));
const leafMat  = () => mat("leaf",  () => new THREE.MeshStandardMaterial({ map: leafTex("green", "#a8d86a", "#5e9440", "#2c4a22"), roughness: 0.9, side: THREE.DoubleSide }));

// ── ⭐ OW-34 · THE ARCHITECT'S PAINT ─────────────────────────────────
// A carpet with a border, a field and a medallion; a runner; a painted
// screen leaf; a banner; and the tapestry, which is the Unstable World in
// thread (the cracked eclipse over the pier, THE RISE on the horizon).
const carpetTex = (key, field, border, gold, w = 256, h = 256) => canvasTex(`min_carpet_${key}`, w, h, (g) => {
  const R = mulberry32(0x5eed0a);
  g.fillStyle = border; g.fillRect(0, 0, w, h);
  const b = Math.round(Math.min(w, h) * 0.11);
  g.fillStyle = field; g.fillRect(b, b, w - 2 * b, h - 2 * b);
  for (let i = 0; i < 420; i++) {                    // the pile
    g.globalAlpha = 0.04 + R() * 0.08;
    g.fillStyle = R() > 0.5 ? "#ffffff" : "#000000";
    g.fillRect(R() * w, R() * h, 1 + R() * 2, 1 + R() * 2);
  }
  g.globalAlpha = 0.9; g.strokeStyle = gold; g.lineWidth = 3;
  g.strokeRect(b - 5, b - 5, w - 2 * b + 10, h - 2 * b + 10);
  g.lineWidth = 1.5; g.strokeRect(b + 6, b + 6, w - 2 * b - 12, h - 2 * b - 12);
  g.fillStyle = gold;                                 // the border's lozenges
  const step = b * 1.6;
  for (let x = step / 2; x < w; x += step) for (const y of [b / 2, h - b / 2]) {
    g.beginPath(); g.moveTo(x, y - b * 0.28); g.lineTo(x + b * 0.28, y); g.lineTo(x, y + b * 0.28); g.lineTo(x - b * 0.28, y); g.fill();
  }
  for (let y = step / 2; y < h; y += step) for (const x of [b / 2, w - b / 2]) {
    g.beginPath(); g.moveTo(x, y - b * 0.28); g.lineTo(x + b * 0.28, y); g.lineTo(x, y + b * 0.28); g.lineTo(x - b * 0.28, y); g.fill();
  }
  const cx = w / 2, cy = h / 2, r = Math.min(w, h) * 0.2;
  if (Math.abs(w - h) < 1) {                          // the medallion, square carpets only
    g.globalAlpha = 0.85; g.lineWidth = 3;
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke();
    g.beginPath(); g.arc(cx, cy, r * 0.62, 0, Math.PI * 2); g.stroke();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      g.beginPath(); g.moveTo(cx + Math.cos(a) * r * 0.62, cy + Math.sin(a) * r * 0.62);
      g.lineTo(cx + Math.cos(a) * r * 1.35, cy + Math.sin(a) * r * 1.35); g.stroke();
    }
    g.beginPath(); g.moveTo(cx, cy - r * 0.4); g.lineTo(cx + r * 0.4, cy); g.lineTo(cx, cy + r * 0.4); g.lineTo(cx - r * 0.4, cy); g.fill();
  } else {                                            // a runner repeats its lozenge down the middle
    g.globalAlpha = 0.7;
    for (let y = h * 0.12; y < h * 0.8; y += h * 0.19) {
      g.beginPath(); g.moveTo(cx, y); g.lineTo(cx + w * 0.22, y + h * 0.06); g.lineTo(cx, y + h * 0.12); g.lineTo(cx - w * 0.22, y + h * 0.06); g.closePath(); g.stroke();
    }
  }
  g.globalAlpha = 1;
});

const leafPanelTex = () => canvasTex("min_screen_leaf", 128, 256, (g) => {
  const R = mulberry32(0x5eed0b);
  const grd = g.createLinearGradient(0, 0, 0, 256);
  grd.addColorStop(0, "#3a2e52"); grd.addColorStop(1, "#1c1628");
  g.fillStyle = grd; g.fillRect(0, 0, 128, 256);
  g.globalAlpha = 0.55; g.strokeStyle = "#c9a84c"; g.lineWidth = 2;   // a painted bough
  g.beginPath(); g.moveTo(20, 240); g.bezierCurveTo(40, 170, 90, 150, 104, 60); g.stroke();
  for (let i = 0; i < 14; i++) stroke(g, 30 + R() * 70, 60 + R() * 170, 14 + R() * 10, 4, R() * Math.PI, R() > 0.5 ? "#d9b8ff" : "#8a6fd4", 0.5 + R() * 0.4);
  g.globalAlpha = 0.8; g.fillStyle = "#f0d060";                       // a small cracked moon
  g.beginPath(); g.arc(88, 36, 12, 0, Math.PI * 2); g.fill();
  g.fillStyle = "#1c1628"; g.fillRect(86, 22, 3, 28);
  g.globalAlpha = 1;
});

const bannerTex = () => canvasTex("min_wall_banner", 128, 256, (g) => {
  g.fillStyle = "#5a2a7a"; g.fillRect(0, 0, 128, 256);
  g.fillStyle = "#3a1a52"; g.fillRect(0, 0, 14, 256); g.fillRect(114, 0, 14, 256);
  g.fillStyle = "#c9a84c"; g.fillRect(14, 0, 4, 256); g.fillRect(110, 0, 4, 256);
  g.strokeStyle = "#f0d060"; g.lineWidth = 4;                          // the Empire's diamond
  g.beginPath(); g.moveTo(64, 70); g.lineTo(96, 118); g.lineTo(64, 166); g.lineTo(32, 118); g.closePath(); g.stroke();
  g.fillStyle = "#f0d060";
  g.beginPath(); g.moveTo(64, 96); g.lineTo(80, 118); g.lineTo(64, 140); g.lineTo(48, 118); g.fill();
  g.fillStyle = "#c9a84c"; g.fillRect(24, 24, 80, 4); g.fillRect(24, 212, 80, 4);
});

const tapestryTex = () => canvasTex("min_tapestry", 512, 384, (g) => {
  const R = mulberry32(0x5eed0c);
  const sky = g.createLinearGradient(0, 0, 0, 384);
  sky.addColorStop(0, "#140a24"); sky.addColorStop(0.55, "#3a1a4e"); sky.addColorStop(0.62, "#7a3a6a"); sky.addColorStop(1, "#0a1e2c");
  g.fillStyle = sky; g.fillRect(0, 0, 512, 384);
  for (let i = 0; i < 90; i++) { g.globalAlpha = 0.3 + R() * 0.6; g.fillStyle = "#fff4d8"; g.fillRect(R() * 512, R() * 200, 1.5, 1.5); }
  g.globalAlpha = 1;
  // THE RISE on the horizon
  const rise = g.createRadialGradient(256, 236, 4, 256, 236, 120);
  rise.addColorStop(0, "rgba(255,190,120,0.9)"); rise.addColorStop(1, "rgba(255,120,160,0)");
  g.fillStyle = rise; g.fillRect(0, 0, 512, 384);
  // the cracked eclipse — rimmed in O'mnis, cracked by N'ullus
  g.fillStyle = "#07040e"; g.beginPath(); g.arc(256, 96, 50, 0, Math.PI * 2); g.fill();
  g.strokeStyle = "#f0d060"; g.lineWidth = 6; g.beginPath(); g.arc(256, 96, 52, 0, Math.PI * 2); g.stroke();
  g.strokeStyle = "#B388FF"; g.lineWidth = 3;
  g.beginPath(); g.moveTo(232, 48); g.lineTo(250, 84); g.lineTo(240, 104); g.lineTo(262, 148); g.stroke();
  // the Chao sea, in thread
  for (let y = 250; y < 384; y += 7) {
    g.globalAlpha = 0.35 + R() * 0.3; g.strokeStyle = R() > 0.5 ? "#3fb6d4" : "#1c6a8a"; g.lineWidth = 2;
    g.beginPath(); g.moveTo(0, y); for (let x = 0; x <= 512; x += 32) g.lineTo(x, y + Math.sin(x * 0.05 + y) * 2); g.stroke();
  }
  g.globalAlpha = 1;
  // the pier, black against it, with its lamps
  g.fillStyle = "#0a0610"; g.fillRect(40, 244, 432, 12);
  for (let x = 56; x < 470; x += 38) g.fillRect(x, 256, 5, 50);
  g.fillStyle = "#f2c14e"; for (let x = 70; x < 460; x += 76) { g.fillRect(x, 226, 3, 18); g.fillRect(x - 3, 222, 9, 6); }
  // the weave and the gold border
  g.globalAlpha = 0.12; g.fillStyle = "#000";
  for (let y = 0; y < 384; y += 3) g.fillRect(0, y, 512, 1);
  g.globalAlpha = 1; g.strokeStyle = "#c9a84c"; g.lineWidth = 14; g.strokeRect(7, 7, 498, 370);
  g.strokeStyle = "#5a2a7a"; g.lineWidth = 4; g.strokeRect(18, 18, 476, 348);
});

const stoneWallTex = () => canvasTex("min_partition_stone_v2", 320, 128, (g) => {
  const R = mulberry32(0x5eed0d);
  g.fillStyle = "#4a3e5c"; g.fillRect(0, 0, 320, 128);          // the mortar, dark
  for (let row = 0; row < 4; row++) {                  // dressed courses, the Hall's own
    const y = row * 32, off = row % 2 ? 32 : 0;
    for (let x = -off; x < 320; x += 64) {
      const v = R();
      const grd = g.createLinearGradient(0, y, 0, y + 32);
      grd.addColorStop(0, `rgb(${222 + v * 18},${214 + v * 18},${232 + v * 14})`);
      grd.addColorStop(1, `rgb(${170 + v * 20},${160 + v * 20},${186 + v * 18})`);
      g.fillStyle = grd;
      g.fillRect(x + 3, y + 3, 58, 26);
      g.globalAlpha = 0.5; g.fillStyle = "#fff8ff"; g.fillRect(x + 3, y + 3, 58, 2);   // a lit arris
      g.globalAlpha = 1;
    }
  }
  g.globalAlpha = 0.22; g.fillStyle = "#5a4a70";
  for (let i = 0; i < 500; i++) g.fillRect(R() * 320, R() * 128, 1 + R() * 2, 1 + R() * 2);
  g.globalAlpha = 1;
});

// ⭐ ROOM FURNITURE PAINT — spines on a shelf are ONE painted plane, never
// forty boxes; a quilt is cloth, a mattress is straw.
const spinesTex = () => canvasTex("min_book_spines", 256, 64, (g) => {
  const R = mulberry32(0x5eed0e);
  g.fillStyle = "#1a1208"; g.fillRect(0, 0, 256, 64);
  const cols = ["#5a2a7a", "#2a4a6a", "#6a3a24", "#2e5a3a", "#7a2a2a", "#c9a84c", "#3a3050", "#8a6a3a"];
  let x = 2;
  while (x < 250) {
    const w = 6 + R() * 10, h = 40 + R() * 22, lean = R() > 0.9;
    g.fillStyle = cols[Math.floor(R() * cols.length)];
    if (lean) { g.save(); g.translate(x, 64); g.rotate(-0.18); g.fillRect(0, -h, w, h); g.restore(); }
    else g.fillRect(x, 64 - h, w, h);
    g.globalAlpha = 0.55; g.fillStyle = "#f0d060";
    g.fillRect(x + 1, 64 - h + 6, w - 2, 1.5); g.fillRect(x + 1, 64 - h + 10, w - 2, 1);
    g.globalAlpha = 1;
    x += w + 1 + (R() > 0.85 ? 6 : 0);
  }
});
const quiltTex = () => canvasTex("min_quilt", 256, 256, (g) => {
  const cells = 6, c = 256 / cells;
  const cols = ["#5a2a7a", "#7a4a8a", "#3a2458", "#8a6a3a"];
  for (let i = 0; i < cells; i++) for (let j = 0; j < cells; j++) {
    g.fillStyle = cols[(i * 3 + j * 5) % cols.length]; g.fillRect(i * c, j * c, c, c);
    g.strokeStyle = "#c9a84c"; g.globalAlpha = 0.5; g.setLineDash([3, 3]); g.strokeRect(i * c + 3, j * c + 3, c - 6, c - 6);
    g.setLineDash([]); g.globalAlpha = 1;
  }
});
// four shelves, four different rows — the same canvas, shifted per shelf
const spinesMat = (k = 0) => mat(`spines_${k}`, () => {
  const t = spinesTex().clone();
  t.wrapS = THREE.RepeatWrapping; t.offset.x = k * 0.27; t.repeat.x = 0.78; t.needsUpdate = true;
  return new THREE.MeshStandardMaterial({ map: t, roughness: 0.85 });
});
const quiltMat  = () => mat("quilt",  () => new THREE.MeshStandardMaterial({ map: quiltTex(), roughness: 0.95 }));
const linenMat  = () => mat("linen",  () => new THREE.MeshStandardMaterial({ color: "#e8e0d0", roughness: 0.95 }));
const darkWoodMat = () => mat("darkwood", () => new THREE.MeshStandardMaterial({ map: woodTex("dark", "#5a3a2a", "#3a2418", "#1a0e08"), roughness: 0.7 }));
// opaque: a transparent double-sided mesh sorts per object, and the coverlet
// showed the mattress through it (review)
const canopyMat = () => mat("canopy", () => new THREE.MeshStandardMaterial({ map: clothTex("canopy", "#b48aff", "#5a2a7a", "#2a1040"), roughness: 0.9, side: THREE.DoubleSide }));
const emberMat  = () => mat("embers", () => new THREE.MeshBasicMaterial({ color: "#ff5a1a", toneMapped: false }));

const rugMat2    = () => mat("rug2",    () => new THREE.MeshStandardMaterial({ map: carpetTex("parlour", "#6a2a3e", "#2a1220", "#c9a84c"), roughness: 0.98 }));
const runnerMat  = () => mat("runner",  () => new THREE.MeshStandardMaterial({ map: carpetTex("runner", "#3a2458", "#1a1030", "#c9a84c", 96, 288), roughness: 0.98 }));
const grandMat   = () => mat("grand",   () => new THREE.MeshStandardMaterial({ map: carpetTex("grand", "#40206a", "#170c28", "#f0d060", 384, 384), roughness: 0.96 }));
const screenMat  = () => mat("screen",  () => new THREE.MeshStandardMaterial({ map: leafPanelTex(), roughness: 0.9, side: THREE.DoubleSide }));
const bannerMat  = () => mat("banner",  () => new THREE.MeshStandardMaterial({ map: bannerTex(), roughness: 0.92, side: THREE.DoubleSide, emissive: "#3a1a52", emissiveIntensity: 0.2 }));
const tapestryMat = () => mat("tapestry", () => new THREE.MeshStandardMaterial({ map: tapestryTex(), roughness: 0.94, side: THREE.DoubleSide, emissive: "#ffffff", emissiveMap: tapestryTex(), emissiveIntensity: 0.18 }));
// the texture is drawn at the partition's own 5:2 face, so no repeat (and no
// cloned texture outside the cache for release to miss)
const partitionMat = () => mat("partition", () => new THREE.MeshStandardMaterial({ map: stoneWallTex(), roughness: 0.82 }));
const hedgeMat   = () => mat("hedge",   () => new THREE.MeshStandardMaterial({ map: leafTex("hedge", "#8ac85a", "#3e7a34", "#1c3a1a"), roughness: 0.95 }));
const ironMat    = () => mat("iron",    () => new THREE.MeshStandardMaterial({ color: "#221c2c", roughness: 0.5, metalness: 0.7 }));
const glassMat   = () => mat("glass",   () => new THREE.MeshStandardMaterial({ color: "#9ad4c8", roughness: 0.15, metalness: 0.1, transparent: true, opacity: 0.55 }));
const bookMat = (c) => mat(`book_${c}`, () => new THREE.MeshStandardMaterial({ color: c, roughness: 0.8 }));

// Glowing things are BASIC + toneMapped false, so they burn past white
// without costing a light. This is the entire lighting budget trick.
const glowMat = (color, opacity = 1) =>
  mat(`glow_${color}_${opacity}`, () => new THREE.MeshBasicMaterial({
    color, transparent: opacity < 1, opacity, toneMapped: false, depthWrite: opacity >= 1,
  }));

// Gaseous things additionally kill depthWrite, which is the whole trick
// for vapour: without it overlapping billows z-fight and stack like
// solid beads; with it they melt into one volume.
const vaporMat = (color, opacity = 0.22) =>
  mat(`vapor_${color}_${opacity}`, () => new THREE.MeshBasicMaterial({
    color, transparent: true, opacity, depthWrite: false, toneMapped: false,
  }));

// ════════════════════════════════════════════════════════════════════
// THE RECIPES
// ────────────────────────────────────────────────────────────────────
// Every one is a plain component taking { fill } (growers only, 0..1)
// and drawing at the origin, standing on y=0, facing +Z. Rotation,
// position and stack lift are the caller's business — this file has no
// opinion about where a thing is, exactly as the being kit has none.
// ════════════════════════════════════════════════════════════════════

// ── FABLE ───────────────────────────────────────────────────────────

function CropBed({ fill = 0 }) {
  // Six sprouts that rise and colour with the barn. The bed itself
  // never changes; the CROP is the readout, which is how a player can
  // tell a full plot from an empty one across the yard.
  const sprouts = useMemo(() => {
    const R = mulberry32(0x11a7);
    return Array.from({ length: 6 }, (_, i) => ({
      x: -0.3 + (i % 3) * 0.3, z: -0.16 + Math.floor(i / 3) * 0.32,
      lean: (R() - 0.5) * 0.5, scale: 0.8 + R() * 0.45,
    }));
  }, []);
  const h = 0.06 + fill * 0.34;
  return (
    <group>
      <mesh position={[0, 0.07, 0]} material={soilMat()}>
        <boxGeometry args={[0.92, 0.14, 0.92]} />
      </mesh>
      {/* the frame — four thin planks, the honest box exception */}
      {[[0, 0.48], [0, -0.48], [0.48, 0], [-0.48, 0]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.11, z]} rotation={[0, i > 1 ? Math.PI / 2 : 0, 0]} material={woodMat()}>
          <boxGeometry args={[1, 0.1, 0.08]} />
        </mesh>
      ))}
      {sprouts.map((s, i) => (
        <group key={i} position={[s.x, 0.14, s.z]} rotation={[s.lean, 0, s.lean * 0.6]}>
          <mesh position={[0, h * s.scale * 0.5, 0]} material={leafMat()}>
            <cylinderGeometry args={[0.012, 0.02, h * s.scale, 5]} />
          </mesh>
          {fill > 0.35 && (
            <mesh position={[0, h * s.scale, 0]} scale={[1, 0.7, 1]}>
              <sphereGeometry args={[0.05 + fill * 0.05, 8, 7]} />
              <meshStandardMaterial color="#d8e86a" emissive="#8ad44a" emissiveIntensity={0.15 + fill * 0.4} roughness={0.7} />
            </mesh>
          )}
        </group>
      ))}
    </group>
  );
}

function DawgBowl({ fill = 0 }) {
  return (
    <group>
      <mesh position={[0, 0.09, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.34, 0.26, 0.18, 16]} />
      </mesh>
      <mesh position={[0, 0.17, 0]} material={darkMat()}>
        <cylinderGeometry args={[0.29, 0.29, 0.03, 16]} />
      </mesh>
      {fill > 0.05 && (
        <mesh position={[0, 0.17 + fill * 0.03, 0]}>
          <cylinderGeometry args={[0.26, 0.26, 0.02 + fill * 0.05, 16]} />
          <meshStandardMaterial color="#9ad46a" emissive="#5e9440" emissiveIntensity={0.2 + fill * 0.5} roughness={0.5} />
        </mesh>
      )}
    </group>
  );
}

function WoodenChair() {
  return (
    <group>
      {[[-0.17, -0.17], [0.17, -0.17], [-0.17, 0.17], [0.17, 0.17]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.21, z]} material={woodMat()}>
          <cylinderGeometry args={[0.032, 0.04, 0.42, 6]} />
        </mesh>
      ))}
      <mesh position={[0, 0.45, 0]} material={woodMat()}>
        <boxGeometry args={[0.46, 0.06, 0.46]} />
      </mesh>
      <mesh position={[0, 0.72, -0.2]} material={woodMat()}>
        <boxGeometry args={[0.44, 0.48, 0.05]} />
      </mesh>
    </group>
  );
}

function StrawRug() {
  return (
    <mesh position={[0, 0.015, 0]} rotation={[-Math.PI / 2, 0, 0]} material={rugMat()}>
      <circleGeometry args={[0.62, 20]} />
    </mesh>
  );
}

function FencePost() {
  return (
    <group>
      <mesh position={[0, 0.42, 0]} material={woodMat()}>
        <cylinderGeometry args={[0.055, 0.075, 0.84, 7]} />
      </mesh>
      <mesh position={[0, 0.86, 0]} material={woodMat()}>
        <coneGeometry args={[0.08, 0.13, 7]} />
      </mesh>
      {/* the two rails, so one post still reads as fencing */}
      {[0.34, 0.6].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} rotation={[0, 0, Math.PI / 2]} material={woodMat()}>
          <cylinderGeometry args={[0.028, 0.028, 0.94, 5]} />
        </mesh>
      ))}
    </group>
  );
}

function LanternPost() {
  return (
    <group>
      <mesh position={[0, 0.62, 0]} material={darkMat()}>
        <cylinderGeometry args={[0.04, 0.06, 1.24, 8]} />
      </mesh>
      <mesh position={[0, 1.3, 0]} material={brassMat()}>
        <sphereGeometry args={[0.13, 12, 10]} />
      </mesh>
      {/* the flame is emissive, not a light. Never a light. */}
      <mesh position={[0, 1.3, 0]} material={glowMat("#ffd48a")}>
        <sphereGeometry args={[0.085, 10, 9]} />
      </mesh>
      <mesh position={[0, 1.3, 0]} material={vaporMat("#ffb04a", 0.13)}>
        <sphereGeometry args={[0.3, 12, 10]} />
      </mesh>
    </group>
  );
}

function StonePath() {
  const chips = useMemo(() => {
    const R = mulberry32(0x51ab);
    return Array.from({ length: 4 }, (_, i) => ({
      x: -0.2 + (i % 2) * 0.4, z: -0.2 + Math.floor(i / 2) * 0.4,
      r: 0.17 + R() * 0.06, rot: R() * Math.PI,
    }));
  }, []);
  return (
    <group>
      {chips.map((c, i) => (
        <mesh key={i} position={[c.x, 0.012, c.z]} rotation={[-Math.PI / 2, 0, c.rot]} material={paleMat()}>
          <circleGeometry args={[c.r, 7]} />
        </mesh>
      ))}
    </group>
  );
}

function HayBale() {
  return (
    <group>
      <mesh position={[0, 0.34, 0]} rotation={[0, 0, Math.PI / 2]} material={strawMat()}>
        <cylinderGeometry args={[0.34, 0.34, 0.62, 14]} />
      </mesh>
      {[-0.16, 0.16].map((x, i) => (
        <mesh key={i} position={[x, 0.34, 0]} rotation={[0, 0, Math.PI / 2]} material={darkMat()}>
          <torusGeometry args={[0.345, 0.018, 6, 16]} />
        </mesh>
      ))}
    </group>
  );
}

function Sapling() {
  return (
    <group>
      <mesh position={[0, 0.3, 0]} material={woodMat()}>
        <cylinderGeometry args={[0.035, 0.06, 0.6, 6]} />
      </mesh>
      {[[0, 0.72, 0, 0.24], [0.13, 0.62, 0.07, 0.16], [-0.11, 0.66, -0.08, 0.14]].map((b, i) => (
        <mesh key={i} position={[b[0], b[1], b[2]]} scale={[1, 0.8, 1]} material={leafMat()}>
          <sphereGeometry args={[b[3], 9, 8]} />
        </mesh>
      ))}
    </group>
  );
}

function FlowerBed() {
  const blooms = useMemo(() => {
    const R = mulberry32(0xf10a);
    const cols = ["#ff8ac4", "#8ad8ff", "#ffd24a", "#a86aff", "#5affa8", "#ff6a5a"];
    return Array.from({ length: 9 }, (_, i) => ({
      x: (R() - 0.5) * 0.72, z: (R() - 0.5) * 0.72,
      h: 0.16 + R() * 0.16, c: cols[i % cols.length], r: 0.045 + R() * 0.025,
    }));
  }, []);
  return (
    <group>
      <mesh position={[0, 0.05, 0]} material={soilMat()}>
        <cylinderGeometry args={[0.46, 0.5, 0.1, 14]} />
      </mesh>
      {blooms.map((b, i) => (
        <group key={i} position={[b.x, 0.1, b.z]}>
          <mesh position={[0, b.h / 2, 0]} material={leafMat()}>
            <cylinderGeometry args={[0.009, 0.014, b.h, 4]} />
          </mesh>
          <mesh position={[0, b.h, 0]} scale={[1, 0.6, 1]}>
            <sphereGeometry args={[b.r, 8, 7]} />
            <meshStandardMaterial color={b.c} emissive={b.c} emissiveIntensity={0.22} roughness={0.6} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function Barrel() {
  return (
    <group>
      <mesh position={[0, 0.42, 0]} material={woodMat()}>
        <cylinderGeometry args={[0.3, 0.26, 0.84, 14]} />
      </mesh>
      {[0.16, 0.42, 0.68].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} material={brassMat()}>
          <torusGeometry args={[0.295, 0.017, 6, 18]} />
        </mesh>
      ))}
      <mesh position={[0, 0.845, 0]} rotation={[-Math.PI / 2, 0, 0]} material={darkMat()}>
        <circleGeometry args={[0.3, 14]} />
      </mesh>
    </group>
  );
}

function Torch() {
  // caP'n's own toy: "if you get a torch and you put it inside a stuffed
  // animal or something it looks like it's on fire." So the flame sits
  // LOW on the shaft — stacked on something, it reads as that thing
  // burning rather than as a torch balanced on it.
  return (
    <group>
      <mesh position={[0, 0.26, 0]} material={woodMat()}>
        <cylinderGeometry args={[0.028, 0.04, 0.52, 6]} />
      </mesh>
      <mesh position={[0, 0.54, 0]} material={darkMat()}>
        <cylinderGeometry args={[0.07, 0.05, 0.1, 8]} />
      </mesh>
      <mesh position={[0, 0.68, 0]} scale={[1, 1.7, 1]} material={glowMat("#ffb04a")}>
        <sphereGeometry args={[0.075, 9, 8]} />
      </mesh>
      <mesh position={[0, 0.74, 0]} scale={[1, 1.5, 1]} material={glowMat("#fff0c8")}>
        <sphereGeometry args={[0.04, 8, 7]} />
      </mesh>
      <mesh position={[0, 0.7, 0]} material={vaporMat("#ff6a1a", 0.17)}>
        <sphereGeometry args={[0.24, 10, 9]} />
      </mesh>
    </group>
  );
}

function BirdBath() {
  return (
    <group>
      <mesh position={[0, 0.28, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.09, 0.16, 0.56, 10]} />
      </mesh>
      <mesh position={[0, 0.6, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.36, 0.24, 0.11, 16]} />
      </mesh>
      <mesh position={[0, 0.655, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.32, 16]} />
        <meshBasicMaterial map={waterTex("bath", "#c8f0ff", "#4a9ad8", "#0a1e34")} toneMapped={false} />
      </mesh>
    </group>
  );
}

function SignPost() {
  return (
    <group>
      <mesh position={[0, 0.46, 0]} material={woodMat()}>
        <cylinderGeometry args={[0.04, 0.05, 0.92, 6]} />
      </mesh>
      <mesh position={[0.16, 0.78, 0]} rotation={[0, 0, -0.06]} material={woodMat()}>
        <boxGeometry args={[0.52, 0.2, 0.035]} />
      </mesh>
      <mesh position={[-0.13, 0.55, 0]} rotation={[0, 0, 0.08]} material={woodMat()}>
        <boxGeometry args={[0.4, 0.16, 0.035]} />
      </mesh>
    </group>
  );
}

function PlankDoor() {
  return (
    <group>
      <mesh position={[0, 0.82, 0]} material={woodMat()}>
        <boxGeometry args={[0.82, 1.64, 0.1]} />
      </mesh>
      {[-0.22, 0, 0.22].map((x, i) => (
        <mesh key={i} position={[x, 0.82, 0.055]} material={woodMat()}>
          <boxGeometry args={[0.16, 1.56, 0.02]} />
        </mesh>
      ))}
      <mesh position={[0.28, 0.8, 0.08]} material={brassMat()}>
        <sphereGeometry args={[0.055, 9, 8]} />
      </mesh>
      {/* the frame, so a door standing in open air still reads as a way in */}
      <mesh position={[0, 0.86, -0.04]} material={darkMat()}>
        <boxGeometry args={[0.98, 1.78, 0.06]} />
      </mesh>
    </group>
  );
}

// ── ⭐ OW-34 · FABLE, THE ARCHITECT'S SHELF ─────────────────────────
// Sizes are in YARD TILES (the Hall scales the kit by HALL_PROP_SCALE).
// A floor piece sits a hair above the ground and under everything.
// ⚑ A WALL PIECE IS DRAWN AGAINST THE PLANE z = 0, FACING +Z — the
// renderer carries it to the wall face and turns it to the room.

function ParlourRug() {
  return (
    <mesh position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]} material={rugMat2()}>
      <planeGeometry args={[1.92, 1.92]} />
    </mesh>
  );
}

function HallRunner() {
  return (
    <mesh position={[0, 0.013, 0]} rotation={[-Math.PI / 2, 0, 0]} material={runnerMat()}>
      <planeGeometry args={[0.9, 2.92]} />
    </mesh>
  );
}

function FoldingScreen() {
  // three leaves in a gentle zig-zag, framed in wood, across two tiles
  const leaves = [[-0.62, 0.22], [0, -0.22], [0.62, 0.22]];
  return (
    <group>
      {leaves.map(([x, a], i) => (
        <group key={i} position={[x, 0, 0]} rotation={[0, a, 0]}>
          {[-0.3, 0.3].map((fx, k) => (
            <mesh key={k} position={[fx, 0.68, 0]} material={woodMat()}>
              <cylinderGeometry args={[0.025, 0.028, 1.36, 6]} />
            </mesh>
          ))}
          <mesh position={[0, 1.34, 0]} rotation={[0, 0, Math.PI / 2]} material={woodMat()}>
            <cylinderGeometry args={[0.022, 0.022, 0.62, 6]} />
          </mesh>
          <mesh position={[0, 0.16, 0]} rotation={[0, 0, Math.PI / 2]} material={woodMat()}>
            <cylinderGeometry args={[0.022, 0.022, 0.62, 6]} />
          </mesh>
          <mesh position={[0, 0.75, 0]} material={screenMat()}>
            <planeGeometry args={[0.58, 1.14]} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function TimberArch() {
  return (
    <group>
      {[-0.38, 0.38].map((x, i) => (
        <group key={i}>
          <mesh position={[x, 0.95, 0]} material={woodMat()}>
            <cylinderGeometry args={[0.055, 0.07, 1.9, 7]} />
          </mesh>
          <mesh position={[x, 0.05, 0]} material={darkMat()}>
            <cylinderGeometry args={[0.1, 0.12, 0.1, 8]} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 1.9, 0]} rotation={[0, 0, 0]} material={woodMat()}>
        <torusGeometry args={[0.38, 0.05, 6, 18, Math.PI]} />
      </mesh>
      <mesh position={[0, 2.3, 0]} material={brassMat()}>
        <octahedronGeometry args={[0.07, 0]} />
      </mesh>
    </group>
  );
}

function HedgeRow() {
  // clipped square — overlapping squashed spheres, never a box
  const tufts = [-0.7, -0.35, 0, 0.35, 0.7];
  return (
    <group>
      {tufts.map((x, i) => (
        <mesh key={i} position={[x, 0.46, 0]} scale={[0.3, 0.46, 0.3]} material={hedgeMat()}>
          <sphereGeometry args={[1, 12, 10]} />
        </mesh>
      ))}
      {tufts.slice(0, 4).map((x, i) => (
        <mesh key={`u${i}`} position={[x + 0.18, 0.74, 0]} scale={[0.26, 0.2, 0.26]} material={hedgeMat()}>
          <sphereGeometry args={[1, 10, 8]} />
        </mesh>
      ))}
    </group>
  );
}

function WallBanner() {
  return (
    <group>
      <mesh position={[0, 2.02, 0.07]} rotation={[0, 0, Math.PI / 2]} material={brassMat()}>
        <cylinderGeometry args={[0.018, 0.018, 0.74, 6]} />
      </mesh>
      {[-0.38, 0.38].map((x, i) => (
        <mesh key={i} position={[x, 2.02, 0.07]} material={brassMat()}>
          <sphereGeometry args={[0.034, 8, 6]} />
        </mesh>
      ))}
      <mesh position={[0, 1.46, 0.06]} material={bannerMat()}>
        <planeGeometry args={[0.6, 1.1]} />
      </mesh>
      {/* the swallowtail */}
      {[-0.15, 0.15].map((x, i) => (
        <mesh key={`t${i}`} position={[x, 0.86, 0.061]} rotation={[0, 0, -Math.PI / 2]} material={bannerMat()}>
          <circleGeometry args={[0.15, 3]} />
        </mesh>
      ))}
    </group>
  );
}

function WallSconce({ flame = "#ffb04a" }) {
  return (
    <group>
      <mesh position={[0, 1.62, 0.02]} material={ironMat()}>
        <boxGeometry args={[0.12, 0.26, 0.04]} />
      </mesh>
      <mesh position={[0, 1.62, 0.16]} rotation={[Math.PI / 2, 0, 0]} material={ironMat()}>
        <cylinderGeometry args={[0.018, 0.018, 0.28, 6]} />
      </mesh>
      <mesh position={[0, 1.68, 0.3]} material={brassMat()}>
        <cylinderGeometry args={[0.085, 0.04, 0.09, 10]} />
      </mesh>
      <mesh position={[0, 1.8, 0.3]} scale={[1, 1.7, 1]} material={glowMat(flame)}>
        <sphereGeometry args={[0.05, 9, 8]} />
      </mesh>
      <mesh position={[0, 1.84, 0.3]} scale={[1, 1.5, 1]} material={glowMat("#fff0c8")}>
        <sphereGeometry args={[0.024, 8, 7]} />
      </mesh>
      <mesh position={[0, 1.78, 0.28]} material={vaporMat(flame, 0.16)}>
        <sphereGeometry args={[0.26, 10, 9]} />
      </mesh>
      {/* the warm wash it paints on the stone behind it */}
      <mesh position={[0, 1.72, 0.012]} material={vaporMat(flame, 0.12)}>
        <circleGeometry args={[0.42, 20]} />
      </mesh>
    </group>
  );
}

function WallShelf() {
  const books = [["#5a2a7a", 0.3, 0.05], ["#2a4a6a", 0.26, 0.045], ["#6a3a24", 0.32, 0.055]];
  let bx = -0.3;
  return (
    <group>
      <mesh position={[0, 1.3, 0.12]} material={woodMat()}>
        <boxGeometry args={[0.84, 0.045, 0.24]} />
      </mesh>
      {[-0.32, 0.32].map((x, i) => (
        <mesh key={i} position={[x, 1.21, 0.07]} rotation={[-Math.PI / 4, 0, 0]} material={ironMat()}>
          <boxGeometry args={[0.03, 0.2, 0.03]} />
        </mesh>
      ))}
      {books.map(([c, h, t], i) => {
        const x = bx + t / 2; bx += t + 0.012;
        return (
          <mesh key={i} position={[x, 1.3 + 0.022 + h / 2, 0.12]} rotation={[0, 0, i === 2 ? -0.18 : 0]} material={bookMat(c)}>
            <boxGeometry args={[t, h, 0.18]} />
          </mesh>
        );
      })}
      {[[0.12, 0.14], [0.28, 0.2]].map(([x, h], i) => (
        <mesh key={`j${i}`} position={[x, 1.3 + 0.022 + h / 2, 0.12]} material={glassMat()}>
          <cylinderGeometry args={[0.055, 0.06, h, 10]} />
        </mesh>
      ))}
    </group>
  );
}

// ── LEGENDARY · the best still thing there is. ZERO ANIMATION. ──────

function ChaoPool({ fill = 0 }) {
  return (
    <group>
      <mesh position={[0, 0.09, 0]} material={darkMat()}>
        <cylinderGeometry args={[0.94, 1.0, 0.18, 22]} />
      </mesh>
      <mesh position={[0, 0.185, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.84, 22]} />
        <meshBasicMaterial map={waterTex("chao", "#a86aff", "#3a1f6a", "#08040f")} toneMapped={false} />
      </mesh>
      {/* the Chao reaching up. Still — it does not move, by law — but it
          gets TALLER as the barn fills, which is a readout, not motion. */}
      <mesh position={[0, 0.2 + fill * 0.24, 0]} material={vaporMat("#a86aff", 0.2)}>
        <sphereGeometry args={[0.3 + fill * 0.24, 14, 12]} />
      </mesh>
      <mesh position={[0, 0.2 + fill * 0.3, 0]} material={glowMat("#c8a8ff", 0.5)}>
        <sphereGeometry args={[0.09 + fill * 0.07, 10, 9]} />
      </mesh>
    </group>
  );
}

function AnimaTree({ fill = 0 }) {
  const fruit = useMemo(() => {
    const R = mulberry32(0xa71a);
    return Array.from({ length: 7 }, () => ({
      x: (R() - 0.5) * 1.1, y: 1.5 + R() * 0.7, z: (R() - 0.5) * 1.1, r: 0.06 + R() * 0.04,
    }));
  }, []);
  return (
    <group>
      <mesh position={[0, 0.8, 0]} material={woodMat()}>
        <cylinderGeometry args={[0.14, 0.26, 1.6, 9]} />
      </mesh>
      {/* roots — overlapping masses, not joined parts */}
      {[0, 1, 2, 3, 4].map((i) => {
        const a = (i / 5) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(a) * 0.24, 0.08, Math.sin(a) * 0.24]} rotation={[0, -a, 0.5]} material={woodMat()}>
            <cylinderGeometry args={[0.05, 0.09, 0.4, 6]} />
          </mesh>
        );
      })}
      {[[0, 1.9, 0, 0.72], [0.44, 1.66, 0.2, 0.44], [-0.4, 1.72, -0.24, 0.42], [0.1, 2.24, -0.3, 0.38]].map((c, i) => (
        <mesh key={i} position={[c[0], c[1], c[2]]} scale={[1, 0.82, 1]} material={leafMat()}>
          <sphereGeometry args={[c[3], 12, 10]} />
        </mesh>
      ))}
      {fruit.slice(0, Math.max(0, Math.round(fill * fruit.length))).map((f, i) => (
        <mesh key={i} position={[f.x, f.y, f.z]} material={glowMat("#ffd88a")}>
          <sphereGeometry args={[f.r, 9, 8]} />
        </mesh>
      ))}
      <mesh position={[0, 1.9, 0]} material={vaporMat("#7fe8d8", 0.07)}>
        <sphereGeometry args={[1.15, 14, 12]} />
      </mesh>
    </group>
  );
}

function CrestFountain() {
  return (
    <group>
      <mesh position={[0, 0.16, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.92, 1.0, 0.32, 20]} />
      </mesh>
      <mesh position={[0, 0.34, 0]} rotation={[-Math.PI / 2, 0, 0]} material={darkMat()}>
        <circleGeometry args={[0.78, 20]} />
      </mesh>
      <mesh position={[0, 0.62, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.14, 0.24, 0.6, 12]} />
      </mesh>
      <mesh position={[0, 1.0, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.42, 0.2, 0.12, 16]} />
      </mesh>
      {/* the Crest itself, dry and gold */}
      <mesh position={[0, 1.26, 0]} rotation={[0, 0, Math.PI / 4]} material={brassMat()}>
        <boxGeometry args={[0.26, 0.26, 0.05]} />
      </mesh>
    </group>
  );
}

function VoidBench() {
  return (
    <group>
      {[-0.62, 0.62].map((x, i) => (
        <mesh key={i} position={[x, 0.24, 0]} material={darkMat()}>
          <boxGeometry args={[0.14, 0.48, 0.44]} />
        </mesh>
      ))}
      <mesh position={[0, 0.52, 0]} material={darkMat()}>
        <boxGeometry args={[1.5, 0.1, 0.5]} />
      </mesh>
      <mesh position={[0, 0.86, -0.2]} material={darkMat()}>
        <boxGeometry args={[1.44, 0.58, 0.07]} />
      </mesh>
      {/* the seat forgetting which way is down. Still, and unsettling. */}
      <mesh position={[0, 0.5, 0]} rotation={[-Math.PI / 2, 0, 0]} material={vaporMat("#8a5aff", 0.24)}>
        <planeGeometry args={[1.44, 0.46]} />
      </mesh>
    </group>
  );
}

function StarLamp() {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} material={darkMat()}>
        <cylinderGeometry args={[0.2, 0.28, 0.2, 12]} />
      </mesh>
      <mesh position={[0, 0.62, 0]} material={brassMat()}>
        <cylinderGeometry args={[0.022, 0.03, 0.84, 7]} />
      </mesh>
      <mesh position={[0, 1.14, 0]} material={glowMat("#fff4d8")}>
        <sphereGeometry args={[0.1, 12, 10]} />
      </mesh>
      <mesh position={[0, 1.14, 0]} material={vaporMat("#ffd88a", 0.15)}>
        <sphereGeometry args={[0.3, 12, 10]} />
      </mesh>
      <mesh position={[0, 1.14, 0]} material={vaporMat("#8ad8ff", 0.08)}>
        <sphereGeometry args={[0.52, 12, 10]} />
      </mesh>
    </group>
  );
}

function PalePillar() {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.34, 0.4, 0.2, 14]} />
      </mesh>
      <mesh position={[0, 0.82, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.24, 0.28, 1.28, 16]} />
      </mesh>
      {/* fluting — thin boxes are allowed, a box here is just a line */}
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(a) * 0.25, 0.82, Math.sin(a) * 0.25]} rotation={[0, -a, 0]} material={paleMat()}>
            <boxGeometry args={[0.03, 1.24, 0.03]} />
          </mesh>
        );
      })}
      <mesh position={[0, 1.52, 0]} material={paleMat()}>
        <cylinderGeometry args={[0.34, 0.26, 0.14, 14]} />
      </mesh>
    </group>
  );
}

function BrassDoor() {
  return (
    <group>
      <mesh position={[0, 0.88, 0]} material={brassMat()}>
        <boxGeometry args={[0.88, 1.76, 0.11]} />
      </mesh>
      <mesh position={[0, 1.3, 0.062]} material={darkMat()}>
        <circleGeometry args={[0.2, 18]} />
      </mesh>
      <mesh position={[0, 1.3, 0.07]} material={glowMat("#ffd88a", 0.7)}>
        <circleGeometry args={[0.15, 18]} />
      </mesh>
      <mesh position={[0.3, 0.84, 0.09]} material={darkMat()}>
        <sphereGeometry args={[0.06, 10, 9]} />
      </mesh>
      <mesh position={[0, 0.92, -0.05]} material={paleMat()}>
        <boxGeometry args={[1.06, 1.9, 0.07]} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE ASCENSION LAYER  —  what makes a Mythical a Mythical
// ────────────────────────────────────────────────────────────────────
// Drawn OVER its Legendary predecessor's recipe, never instead of it.
// Motion and flare, which is exactly the difference the law names, and
// the reason Legendary is defined as "zero animation": if freezing a
// Mythical turns it back into its Legendary, the pair is correct.
// (The frozen test, from PIERRE_PRIME L23.)
// ════════════════════════════════════════════════════════════════════
function Ascension({ height = 1.6, tint = "#c8a8ff", sx = 1, sz = 1 }) {
  const g = useRef();
  const motes = useRef();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (g.current) {
      g.current.rotation.y = t * 0.34;
      const s = 1 + Math.sin(t * 1.1) * 0.055;
      g.current.scale.set(s, 1, s);
    }
    if (motes.current) {
      motes.current.rotation.y = -t * 0.52;
      motes.current.position.y = height * 0.5 + Math.sin(t * 0.8) * 0.1;
    }
  });
  return (
    <group>
      {/* the ring it rose through */}
      <group scale={[sx, 1, sz]}>
      <group ref={g} position={[0, 0.04, 0]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} material={glowMat(tint, 0.5)}>
          <ringGeometry args={[0.52, 0.62, 26]} />
        </mesh>
        <mesh rotation={[-Math.PI / 2, 0, 0]} material={vaporMat(tint, 0.12)}>
          <circleGeometry args={[0.78, 26]} />
        </mesh>
      </group>
      {/* the column of held light */}
      <mesh position={[0, height * 0.5, 0]} material={vaporMat(tint, 0.07)}>
        <cylinderGeometry args={[0.44, 0.6, height, 14, 1, true]} />
      </mesh>
      </group>
      {/* six motes, fixed phases, no PRNG anywhere near a render */}
      <group ref={motes}>
        {Array.from({ length: 6 }, (_, i) => {
          const a = (i / 6) * Math.PI * 2;
          return (
            <mesh key={i} position={[Math.cos(a) * 0.5, Math.sin(a * 1.7) * 0.22, Math.sin(a) * 0.5]} material={glowMat("#fff4d8")}>
              <sphereGeometry args={[0.032, 7, 6]} />
            </mesh>
          );
        })}
      </group>
      <mesh position={[0, height + 0.16, 0]} material={glowMat(tint, 0.85)}>
        <octahedronGeometry args={[0.12, 0]} />
      </mesh>
    </group>
  );
}

// ── THE RIPE HALO ───────────────────────────────────────────────────
// (2026-09-15) A ripe grower was invisible from across the yard: the
// crop's own readout is `fill`, and after ONE cycle of a 96-cycle barn
// fill is 0.01 — sprouts a millimetre taller. The only signal was a DOM
// chip. So every grower with at least one cycle waiting wears this: a
// slow breathing ring on the ground and three motes climbing. Generic on
// purpose — one component, every grower, present and future — and drawn
// by MineriumProp so no recipe has to remember it. Basic materials, no
// light, one useFrame.
function RipeHalo({ radius = 0.5 }) {
  const ring = useRef();
  const motes = useRef();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (ring.current) {
      const s = 1 + Math.sin(t * 2.2) * 0.08;
      ring.current.scale.set(s, s, 1);
      ring.current.material.opacity = 0.45 + Math.sin(t * 2.2) * 0.2;
    }
    if (motes.current) {
      motes.current.rotation.y = t * 0.8;
      motes.current.position.y = 0.35 + ((t * 0.5) % 1) * 0.7;
      const k = 1 - ((t * 0.5) % 1);
      motes.current.scale.setScalar(0.6 + k * 0.6);
    }
  });
  return (
    <group>
      <mesh ref={ring} position={[0, 0.035, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[radius * 0.9, radius * 1.05, 28]} />
        <meshBasicMaterial color="#9ad46a" transparent opacity={0.5} toneMapped={false} depthWrite={false} />
      </mesh>
      <group ref={motes}>
        {[0, 1, 2].map((i) => {
          const a = (i / 3) * Math.PI * 2;
          return (
            <mesh key={i} position={[Math.cos(a) * radius * 0.6, 0, Math.sin(a) * radius * 0.6]} material={glowMat("#c8f08a")}>
              <sphereGeometry args={[0.035, 6, 5]} />
            </mesh>
          );
        })}
      </group>
    </group>
  );
}

// ── ⭐ ROOM FURNITURE · FABLE ───────────────────────────────────────

function PlankTable() {
  return (
    <group>
      <mesh position={[0, 0.72, 0]} material={woodMat()}>
        <boxGeometry args={[1.86, 0.07, 0.84]} />
      </mesh>
      {[[-0.82, -0.34], [0.82, -0.34], [-0.82, 0.34], [0.82, 0.34]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.35, z]} material={woodMat()}>
          <cylinderGeometry args={[0.04, 0.05, 0.7, 7]} />
        </mesh>
      ))}
      {[-0.34, 0.34].map((z, i) => (
        <mesh key={`s${i}`} position={[0, 0.2, z]} material={woodMat()}>
          <boxGeometry args={[1.62, 0.05, 0.05]} />
        </mesh>
      ))}
    </group>
  );
}

function TallBookcase() {
  const shelves = [0.12, 0.54, 0.96, 1.38];
  return (
    <group>
      {/* the carcass: sides, top, back */}
      {[-0.93, 0.93].map((x, i) => (
        <mesh key={i} position={[x, 0.9, 0]} material={woodMat()}>
          <boxGeometry args={[0.06, 1.8, 0.4]} />
        </mesh>
      ))}
      <mesh position={[0, 1.82, 0]} material={woodMat()}>
        <boxGeometry args={[1.98, 0.06, 0.44]} />
      </mesh>
      <mesh position={[0, 0.9, -0.18]} material={darkWoodMat()}>
        <boxGeometry args={[1.82, 1.8, 0.03]} />
      </mesh>
      {shelves.map((y, i) => (
        <group key={i}>
          <mesh position={[0, y, 0]} material={woodMat()}>
            <boxGeometry args={[1.82, 0.04, 0.38]} />
          </mesh>
          {/* one painted row of spines per shelf, stood a little proud */}
          <mesh position={[0, y + 0.19, -0.02]} material={spinesMat(i)}>
            <planeGeometry args={[1.78, 0.34]} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function StrawBed() {
  return (
    <group>
      <mesh position={[0, 0.125, 0]} material={woodMat()}>
        <boxGeometry args={[0.9, 0.25, 1.9]} />
      </mesh>
      <mesh position={[0, 0.3, 0.02]} material={strawMat()}>
        <boxGeometry args={[0.82, 0.12, 1.78]} />
      </mesh>
      <mesh position={[0, 0.38, 0.28]} material={quiltMat()}>
        <boxGeometry args={[0.86, 0.05, 1.22]} />
      </mesh>
      <mesh position={[0, 0.41, -0.62]} scale={[1, 0.45, 0.62]} material={linenMat()}>
        <sphereGeometry args={[0.3, 12, 10]} />
      </mesh>
      <mesh position={[0, 0.5, -0.94]} material={woodMat()}>
        <boxGeometry args={[0.9, 0.72, 0.06]} />
      </mesh>
    </group>
  );
}

// ── ⭐ ROOM FURNITURE · LEGENDARY ───────────────────────────────────

function PaleHearth({ flame = "#ff9a3a" }) {
  return (
    <group>
      {/* hearthstone, surround, chimney breast — the Hall's own pale stone */}
      <mesh position={[0, 0.05, 0.1]} material={darkMat()}>
        <boxGeometry args={[1.96, 0.1, 0.7]} />
      </mesh>
      {[-0.72, 0.72].map((x, i) => (
        <mesh key={i} position={[x, 0.55, -0.08]} material={partitionMat()}>
          <boxGeometry args={[0.44, 1.0, 0.5]} />
        </mesh>
      ))}
      <mesh position={[0, 1.1, -0.08]} material={paleMat()}>
        <boxGeometry args={[1.94, 0.14, 0.62]} />
      </mesh>
      <mesh position={[0, 1.04, 0.215]} material={brassMat()}>
        <boxGeometry args={[1.96, 0.03, 0.06]} />
      </mesh>
      <mesh position={[0, 1.62, -0.18]} material={partitionMat()}>
        <boxGeometry args={[1.3, 0.9, 0.32]} />
      </mesh>
      {/* the firebox */}
      <mesh position={[0, 0.55, -0.3]} material={darkMat()}>
        <boxGeometry args={[1.0, 1.0, 0.06]} />
      </mesh>
      {/* logs, embers, and a flame that takes the room's light */}
      {[[-0.14, 0.2], [0.14, -0.2]].map(([x, a], i) => (
        <mesh key={i} position={[x, 0.16, -0.08]} rotation={[0, a, Math.PI / 2]} material={darkWoodMat()}>
          <cylinderGeometry args={[0.05, 0.06, 0.62, 7]} />
        </mesh>
      ))}
      <mesh position={[0, 0.12, -0.08]} rotation={[-Math.PI / 2, 0, 0]} material={emberMat()}>
        <circleGeometry args={[0.26, 14]} />
      </mesh>
      {/* tongues of flame, not a glowing egg (visual check): five thin
          cones leaning out of the logs, a white heart, a faint glow */}
      {[[-0.2, 0.26, 0.2], [-0.08, 0.36, 0.08], [0.04, 0.44, -0.04], [0.15, 0.32, -0.16], [0.24, 0.22, -0.28]].map(([x, h, lean], i) => (
        <mesh key={`f${i}`} position={[x, 0.16 + h / 2, -0.1 + (i % 2) * 0.04]} rotation={[0, 0, lean]} material={glowMat(flame)}>
          <coneGeometry args={[0.055, h, 6]} />
        </mesh>
      ))}
      <mesh position={[0, 0.26, -0.08]} material={glowMat("#fff0c8")}>
        <coneGeometry args={[0.04, 0.2, 6]} />
      </mesh>
      <mesh position={[0, 0.4, -0.06]} material={vaporMat(flame, 0.1)}>
        <sphereGeometry args={[0.22, 12, 10]} />
      </mesh>
      {/* the warm pool it throws on the floor in front */}
      <mesh position={[0, 0.03, 0.3]} rotation={[-Math.PI / 2, 0, 0]} scale={[1.4, 0.35, 1]} material={vaporMat(flame, 0.14)}>
        <circleGeometry args={[0.6, 20]} />
      </mesh>
    </group>
  );
}

function VoidFourPoster() {
  const posts = [[-0.9, -1.4], [0.9, -1.4], [-0.9, 1.4], [0.9, 1.4]];
  return (
    <group>
      <mesh position={[0, 0.22, 0]} material={darkWoodMat()}>
        <boxGeometry args={[1.8, 0.26, 2.84]} />
      </mesh>
      <mesh position={[0, 0.42, 0.04]} material={linenMat()}>
        <boxGeometry args={[1.66, 0.16, 2.66]} />
      </mesh>
      <mesh position={[0, 0.52, 0.34]} material={canopyMat()}>
        <boxGeometry args={[1.72, 0.06, 1.96]} />
      </mesh>
      {[-0.4, 0.4].map((x, i) => (
        <mesh key={i} position={[x, 0.58, -1.0]} scale={[1, 0.42, 0.62]} material={linenMat()}>
          <sphereGeometry args={[0.32, 12, 10]} />
        </mesh>
      ))}
      <mesh position={[0, 0.72, -1.38]} material={darkWoodMat()}>
        <boxGeometry args={[1.76, 0.92, 0.08]} />
      </mesh>
      {posts.map(([x, z], i) => (
        <group key={i}>
          <mesh position={[x, 1.02, z]} material={darkWoodMat()}>
            <cylinderGeometry args={[0.05, 0.06, 2.04, 8]} />
          </mesh>
          <mesh position={[x, 2.08, z]} material={brassMat()}>
            <sphereGeometry args={[0.06, 8, 6]} />
          </mesh>
        </group>
      ))}
      {/* the canopy and its drapes, gathered at the posts */}
      <mesh position={[0, 2.02, 0]} rotation={[-Math.PI / 2, 0, 0]} material={canopyMat()}>
        <planeGeometry args={[1.84, 2.84]} />
      </mesh>
      {posts.map(([x, z], i) => (
        <mesh key={`d${i}`} position={[x * 0.94, 1.5, z * 0.97]} rotation={[0, (x > 0) === (z > 0) ? 0.5 : -0.5, 0]} material={canopyMat()}>
          <planeGeometry args={[0.36, 1.04]} />
        </mesh>
      ))}
    </group>
  );
}

function LongTable() {
  return (
    <group>
      <mesh position={[0, 0.74, 0]} material={darkWoodMat()}>
        <boxGeometry args={[2.86, 0.08, 0.86]} />
      </mesh>
      {[-1.3, 0, 1.3].map((x) => [-0.34, 0.34].map((z) => (
        <mesh key={`${x}|${z}`} position={[x, 0.36, z]} material={darkWoodMat()}>
          <cylinderGeometry args={[0.045, 0.06, 0.72, 8]} />
        </mesh>
      )))}
      <mesh position={[0, 0.79, 0]} material={canopyMat()}>
        <boxGeometry args={[2.4, 0.01, 0.3]} />
      </mesh>
      {[-0.8, 0.8].map((x, i) => (
        <group key={i} position={[x, 0.78, 0]}>
          <mesh position={[0, 0.16, 0]} material={brassMat()}>
            <cylinderGeometry args={[0.02, 0.07, 0.32, 8]} />
          </mesh>
          {[-0.12, 0, 0.12].map((dx, k) => (
            <group key={k}>
              <mesh position={[dx, 0.36, 0]} material={linenMat()}>
                <cylinderGeometry args={[0.018, 0.018, 0.1, 6]} />
              </mesh>
              <mesh position={[dx, 0.44, 0]} scale={[1, 1.7, 1]} material={glowMat("#ffc978")}>
                <sphereGeometry args={[0.016, 6, 5]} />
              </mesh>
            </group>
          ))}
          <mesh position={[0, 0.3, 0]} rotation={[0, 0, Math.PI / 2]} material={brassMat()}>
            <cylinderGeometry args={[0.012, 0.012, 0.26, 5]} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// ── ⭐ OW-34 · LEGENDARY, THE ARCHITECT'S SHELF ─────────────────────

function GrandCarpet() {
  return (
    <group>
      <mesh position={[0, 0.014, 0]} rotation={[-Math.PI / 2, 0, 0]} material={grandMat()}>
        <planeGeometry args={[2.9, 2.9]} />
      </mesh>
      {/* the fringe, on the two ends the weave runs to */}
      {[-1.47, 1.47].map((z, i) => (
        <mesh key={i} position={[0, 0.016, z]} rotation={[-Math.PI / 2, 0, 0]} material={brassMat()}>
          <planeGeometry args={[2.7, 0.05]} />
        </mesh>
      ))}
    </group>
  );
}

function StonePartition() {
  // Waist-to-shoulder, not floor-to-ceiling: the Hall's lens looks down
  // over it, so a wall you can see over is a room you can still play in.
  return (
    <group>
      <mesh position={[0, 0.09, 0]} material={darkMat()}>
        <boxGeometry args={[2.96, 0.18, 0.46]} />
      </mesh>
      <mesh position={[0, 0.76, 0]} material={partitionMat()}>
        <boxGeometry args={[2.88, 1.16, 0.3]} />
      </mesh>
      <mesh position={[0, 1.38, 0]} material={paleMat()}>
        <boxGeometry args={[2.96, 0.08, 0.4]} />
      </mesh>
      <mesh position={[0, 1.34, 0]} material={brassMat()}>
        <boxGeometry args={[2.98, 0.03, 0.42]} />
      </mesh>
      {/* the dado rail, the Hall's own detail */}
      {[0.16, -0.16].map((z, i) => (
        <mesh key={i} position={[0, 0.42, z]} material={brassMat()}>
          <boxGeometry args={[2.9, 0.035, 0.02]} />
        </mesh>
      ))}
      {/* the end piers stand proud of the wall, capped in gold, so the
          partition reads as architecture and not as a crate */}
      {[-1.4, 1.4].map((x, i) => (
        <group key={`p${i}`}>
          <mesh position={[x, 0.78, 0]} material={darkMat()}>
            <boxGeometry args={[0.2, 1.36, 0.38]} />
          </mesh>
          <mesh position={[x, 1.5, 0]} material={brassMat()}>
            <octahedronGeometry args={[0.09, 0]} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function PaleArchway() {
  return (
    <group>
      {[-0.38, 0.38].map((x, i) => (
        <group key={i}>
          <mesh position={[x, 0.08, 0]} material={paleMat()}>
            <boxGeometry args={[0.22, 0.16, 0.26]} />
          </mesh>
          <mesh position={[x, 1.0, 0]} material={paleMat()}>
            <cylinderGeometry args={[0.075, 0.085, 1.68, 12]} />
          </mesh>
          <mesh position={[x, 1.88, 0]} material={paleMat()}>
            <boxGeometry args={[0.22, 0.1, 0.24]} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 1.93, 0]} material={paleMat()}>
        <torusGeometry args={[0.38, 0.07, 8, 20, Math.PI]} />
      </mesh>
      <mesh position={[0, 2.33, 0]} material={brassMat()}>
        <boxGeometry args={[0.14, 0.16, 0.18]} />
      </mesh>
    </group>
  );
}

function GrandTapestry() {
  return (
    <group>
      {/* ⚠ kept under kit y 2.15 — ×1.25 in the Hall is the plaque band at 2.69 */}
      <mesh position={[0, 2.08, 0.08]} rotation={[0, 0, Math.PI / 2]} material={brassMat()}>
        <cylinderGeometry args={[0.022, 0.022, 1.86, 8]} />
      </mesh>
      {[-0.93, 0.93].map((x, i) => (
        <mesh key={i} position={[x, 2.08, 0.08]} material={brassMat()}>
          <octahedronGeometry args={[0.05, 0]} />
        </mesh>
      ))}
      <mesh position={[0, 1.38, 0.06]} material={tapestryMat()}>
        <planeGeometry args={[1.8, 1.35]} />
      </mesh>
      {/* tassels */}
      {[-0.8, -0.4, 0, 0.4, 0.8].map((x, i) => (
        <mesh key={`t${i}`} position={[x, 0.64, 0.06]} material={brassMat()}>
          <coneGeometry args={[0.03, 0.12, 6]} />
        </mesh>
      ))}
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE REGISTRY
// ────────────────────────────────────────────────────────────────────
// ⚠ MYTHICALS ARE ABSENT FROM THIS TABLE ON PURPOSE, and that absence
// is the enforcement. There is no row to give them a different shape.
// ════════════════════════════════════════════════════════════════════
export const PROP_ART = {
  crop_bed: CropBed,
  dawg_bowl: DawgBowl,
  wooden_chair: WoodenChair,
  straw_rug: StrawRug,
  fence_post: FencePost,
  lantern_post: LanternPost,
  stone_path: StonePath,
  hay_bale: HayBale,
  sapling: Sapling,
  flower_bed: FlowerBed,
  barrel: Barrel,
  torch: Torch,
  bird_bath: BirdBath,
  sign_post: SignPost,
  plank_door: PlankDoor,
  chao_pool: ChaoPool,
  anima_tree: AnimaTree,
  crest_fountain: CrestFountain,
  void_bench: VoidBench,
  star_lamp: StarLamp,
  pale_pillar: PalePillar,
  brass_door: BrassDoor,
  // OW-34
  parlour_rug: ParlourRug,
  hall_runner: HallRunner,
  folding_screen: FoldingScreen,
  timber_arch: TimberArch,
  hedge_row: HedgeRow,
  wall_banner: WallBanner,
  wall_sconce: WallSconce,
  wall_shelf: WallShelf,
  grand_carpet: GrandCarpet,
  stone_partition: StonePartition,
  pale_archway: PaleArchway,
  grand_tapestry: GrandTapestry,
  // room furniture
  plank_table: PlankTable,
  tall_bookcase: TallBookcase,
  straw_bed: StrawBed,
  pale_hearth: PaleHearth,
  void_four_poster: VoidFourPoster,
  long_table: LongTable,
};

// Every drawable art key the catalogue can resolve to. Used by the
// audit — a catalogue row pointing at a recipe that does not exist must
// be found at boot, not by a player walking into an empty patch of yard.
export const ART_IDS = Object.keys(PROP_ART);

// ⚑ THE ONE COMPONENT EVERYTHING ELSE MOUNTS. Nothing outside this file
// reaches into PROP_ART, so the Mythical resolution cannot be bypassed
// by a caller who "just needs the shape."
// `flame` — the Hall's mood tints any flame a piece carries (OW-34).
export function MineriumProp({ itemId, fill = 0, ripe = false, flame = undefined }) {
  const item = ITEMS[itemId];
  const artKey = artOf(itemId);
  const Art = artKey ? PROP_ART[artKey] : null;
  // A missing recipe renders NOTHING — not a grey box, not a crash. The
  // gap is obvious the first time you walk past it and it takes nobody's
  // holding down with it.
  if (!item || !Art) return null;
  const ascended = isAscended(itemId);
  const [sw, sd] = sizeOf(itemId);
  const onWall = layerOf(itemId) === LAYER.WALL;
  const flat = layerOf(itemId) === LAYER.FLOOR;
  return (
    <group>
      <Art fill={fill} flame={flame} />
      {ripe && item.kind === "grower" && <RipeHalo radius={(item.footprint || 1) * 0.5 + 0.1} />}
      {/* ⚠ `tint` was never passed, so every Mythical in the Empire
          ascended in `#c8a8ff` — a fourth violet that appears in no
          palette — while its own shelf tint is `#a78bfa`. The ascension
          IS the tier made visible; painting it in a colour the tier does
          not use is the one place that must not drift. */}
      {/* ⭐ OW-34: a risen piece rises the way it is SHAPED — a carpet's
          ring spreads under the whole weave, a partition's along its
          length, and a tapestry's halo stands on the wall behind it. */}
      {ascended && !onWall && (
        // the disc (r 0.78) is scaled to the piece's own half-span, never past it
        <Ascension height={flat ? 0.5 : (sw > 1 || sd > 1 ? 1.9 : 1.6)}
                   sx={Math.max(1, sw * 0.62)} sz={Math.max(1, sd * 0.62)}
                   tint={(SHELF_META[item.shelf] || SHELF_META.mythical).tint} />
      )}
      {ascended && onWall && (
        // a halo in FRONT of the hanging, framing it, under the plaque band
        <group position={[0, 1.38, 0.1]} rotation={[Math.PI / 2, 0, 0]}>
          <Ascension height={0.05} sx={sw * 0.72} sz={1.0}
                     tint={(SHELF_META[item.shelf] || SHELF_META.mythical).tint} />
        </group>
      )}
    </group>
  );
}

// Boot-time check that the catalogue and this kit have not drifted.
// Same shape as `auditCatalogue()` and for the same reason.
export function auditPropArt() {
  const problems = [];
  for (const id of Object.keys(ITEMS)) {
    if (isWorn(id)) continue;                 // skies, grounds and the Hall's finishes are tints, not shapes
    const key = artOf(id);
    if (!key) { problems.push(`${id}: resolves to no art key`); continue; }
    if (!PROP_ART[key]) problems.push(`${id}: art "${key}" has no recipe in mineriumProps.jsx`);
  }
  for (const key of ART_IDS) {
    const used = Object.keys(ITEMS).some((id) => artOf(id) === key);
    if (!used) problems.push(`mineriumProps.jsx: recipe "${key}" is drawn by nothing in the catalogue`);
  }
  return problems;
}

export default MineriumProp;
